import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertCircle, 
  AlertTriangle, 
  XCircle, 
  Info,
  X,
  Copy,
  Mail,
  RefreshCw
} from 'lucide-react';

/**
 * Premium Error Popup Component
 * 
 * A sophisticated error display component with:
 * - Error code display
 * - Categorized error types
 * - Calm entrance animation
 * - Copy error code functionality
 * - Contact support action
 * - Retry functionality
 */

const ErrorPopup = ({ 
  error, 
  onClose, 
  onRetry,
  autoClose = false,
  autoCloseDuration = 5000,
  showRetry = false,
  showContact = true
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (autoClose && error) {
      const timer = setTimeout(() => {
        onClose && onClose();
      }, autoCloseDuration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDuration, error, onClose]);

  if (!error) return null;

  // Soft status tint behind the icon; the colour tells the severity apart.
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'error':
        return {
          icon: XCircle,
          iconColor: 'text-danger-600 dark:text-danger-400',
          iconBg: 'bg-danger-50 dark:bg-danger-500/10'
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          iconColor: 'text-warning-600 dark:text-warning-400',
          iconBg: 'bg-warning-50 dark:bg-warning-500/10'
        };
      case 'info':
        return {
          icon: Info,
          iconColor: 'text-info-600 dark:text-info-400',
          iconBg: 'bg-info-50 dark:bg-info-500/10'
        };
      default:
        return {
          icon: AlertCircle,
          iconColor: 'text-gray-500 dark:text-gray-400',
          iconBg: 'bg-gray-100 dark:bg-white/[0.06]'
        };
    }
  };

  const config = getSeverityConfig(error.severity || 'error');
  const Icon = config.icon;

  const copyErrorCode = () => {
    if (error.code) {
      navigator.clipboard.writeText(error.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleContactSupport = () => {
    const subject = encodeURIComponent(`Support Request - Error ${error.code}`);
    const body = encodeURIComponent(
      `Error Code: ${error.code}\n` +
      `Title: ${error.title}\n` +
      `Timestamp: ${error.timestamp || new Date().toISOString()}\n` +
      `\nPlease describe the issue:\n\n`
    );
    window.location.href = `mailto:support@flexcoa.com?subject=${subject}&body=${body}`;
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const popupVariants = {
    hidden: { 
      opacity: 0, 
      y: 8
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.2,
        ease: 'easeOut'
      }
    },
    exit: { 
      opacity: 0, 
      y: 8,
      transition: {
        duration: 0.15
      }
    }
  };

  return (
    <AnimatePresence>
      {error && (
        <motion.div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.div 
            className="relative w-full max-w-[540px] overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
            variants={popupVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Close Button */}
            {onClose && (
              <button
                onClick={onClose}
                className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                aria-label="Close error popup"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            )}

            {/* Header with Icon and Error Code */}
            <div className="relative px-6 pb-4 pt-8 text-center">
              <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${config.iconBg}`}>
                <Icon className={`h-6 w-6 ${config.iconColor}`} strokeWidth={1.75} />
              </div>
              
              {error.code && (
                <div className="inline-flex items-center gap-3 rounded-lg border border-gray-200/80 bg-gray-50 py-2 pl-3.5 pr-2 dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="text-left">
                    <span className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Error Code
                    </span>
                    <span className="font-mono text-sm font-semibold tracking-wide text-gray-900 dark:text-gray-100">
                      {error.code}
                    </span>
                  </div>
                  <button
                    onClick={copyErrorCode}
                    className="flex h-8 min-w-[2rem] items-center justify-center rounded-md px-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-primary-400"
                    title="Copy error code"
                    aria-label="Copy error code"
                  >
                    {copied ? (
                      <span className="px-1 text-[11px] font-semibold text-primary-700 dark:text-primary-400">
                        Copied!
                      </span>
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="px-6 pb-5">
              <h2 className="mb-2 text-center text-lg font-semibold text-gray-900 dark:text-gray-50">
                {error.title || 'An Error Occurred'}
              </h2>
              
              <p className="mb-1 text-center text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {error.userMessage || error.message || 'An unexpected error occurred. Please try again or contact support.'}
              </p>

              {error.timestamp && (
                <p className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
                  {new Date(error.timestamp).toLocaleString()}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col justify-center gap-2 px-6 pb-5 sm:flex-row">
              {showRetry && onRetry && (
                <button
                  onClick={onRetry}
                  className="btn btn-primary"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
              )}
              
              {showContact && (
                <button
                  onClick={handleContactSupport}
                  className="btn btn-secondary"
                >
                  <Mail className="h-4 w-4" />
                  Contact Support
                </button>
              )}

              {onClose && !showRetry && (
                <button
                  onClick={onClose}
                  className="btn btn-primary"
                >
                  Close
                </button>
              )}
            </div>

            {/* Support Message */}
            <div className="border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
              <p className="text-center text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                If this problem persists, please contact support with the error code above.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ErrorPopup;
